import React, { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Link } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `£${(value / 1000000).toFixed(1)}M`;
  }
  return `£${(value / 1000).toFixed(0)}K`;
};

export default function LoanApplicationForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loanAmount, setLoanAmount] = useState([250000]);
  const [formData, setFormData] = useState({
    full_name: '',
    business_name: '',
    phone_number: '',
    email: '',
    business_type: '',
    company_reg_number: '',
    trading_address: '',
    time_in_business: '',
    loan_type: '',
    funding_purpose: '',
    gdpr_consent: false,
    marketing_opt_in: false,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.gdpr_consent) {
      toast.error('Please accept the GDPR consent to continue');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads`, {
        ...formData,
        loan_amount: loanAmount[0],
      });
      
      // Navigate to thank you page with application data
      navigate('/thank-you', { 
        state: { 
          loan_amount: loanAmount[0],
          business_type: formData.business_type,
          loan_type: formData.loan_type,
          funding_purpose: formData.funding_purpose,
          business_name: formData.business_name
        } 
      });
      
      toast.success('Application submitted successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof message === 'string' ? message : 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 sm:p-8" data-testid="loan-application-form">
      <div className="mb-8">
        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">Get Your Quote</h3>
        <p className="text-slate-600 text-sm">Complete the form below for a quick eligibility check</p>
      </div>

      {/* Loan Amount Slider */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <Label className="text-sm font-medium text-slate-700">Loan Amount Required</Label>
          <span className="text-2xl font-bold text-blue-700 font-['Outfit']" data-testid="loan-amount-display">
            {formatCurrency(loanAmount[0])}
          </span>
        </div>
        
        {/* Slider */}
        <div className="relative px-1">
          <Slider
            value={loanAmount}
            onValueChange={setLoanAmount}
            min={10000}
            max={10000000}
            step={10000}
            className="w-full"
            data-testid="loan-amount-slider"
          />
        </div>
        
        {/* Simple 3-point labels */}
        <div className="flex justify-between text-xs text-slate-500 mt-3 px-1">
          <span>£10K</span>
          <span>£5M</span>
          <span>£10M</span>
        </div>
        
        {/* Drag hint */}
        <p className="text-xs text-slate-400 text-center mt-2">
          ← Drag to select amount →
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {/* Full Name */}
        <div>
          <Label htmlFor="full_name" className="text-sm font-medium text-slate-700">Full Name *</Label>
          <Input
            id="full_name"
            value={formData.full_name}
            onChange={(e) => handleChange('full_name', e.target.value)}
            placeholder="John Smith"
            required
            className="mt-1.5"
            data-testid="input-full-name"
          />
        </div>

        {/* Business Name */}
        <div>
          <Label htmlFor="business_name" className="text-sm font-medium text-slate-700">Business Name *</Label>
          <Input
            id="business_name"
            value={formData.business_name}
            onChange={(e) => handleChange('business_name', e.target.value)}
            placeholder="Acme Ltd"
            required
            className="mt-1.5"
            data-testid="input-business-name"
          />
        </div>

        {/* Phone */}
        <div>
          <Label htmlFor="phone_number" className="text-sm font-medium text-slate-700">Phone Number *</Label>
          <Input
            id="phone_number"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => handleChange('phone_number', e.target.value)}
            placeholder="07123 456789"
            required
            className="mt-1.5"
            data-testid="input-phone"
          />
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="john@acme.co.uk"
            required
            className="mt-1.5"
            data-testid="input-email"
          />
        </div>

        {/* Business Type */}
        <div>
          <Label className="text-sm font-medium text-slate-700">Business Type *</Label>
          <Select value={formData.business_type} onValueChange={(v) => handleChange('business_type', v)} required>
            <SelectTrigger className="mt-1.5" data-testid="select-business-type">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Ltd">Limited Company (Ltd)</SelectItem>
              <SelectItem value="Sole Trader">Sole Trader</SelectItem>
              <SelectItem value="Partnership">Partnership</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Company Reg Number */}
        <div>
          <Label htmlFor="company_reg_number" className="text-sm font-medium text-slate-700">Company Reg Number</Label>
          <Input
            id="company_reg_number"
            value={formData.company_reg_number}
            onChange={(e) => handleChange('company_reg_number', e.target.value)}
            placeholder="12345678"
            className="mt-1.5"
            data-testid="input-company-reg"
          />
        </div>

        {/* Trading Address */}
        <div className="sm:col-span-2">
          <Label htmlFor="trading_address" className="text-sm font-medium text-slate-700">Trading Address / Postcode *</Label>
          <Input
            id="trading_address"
            value={formData.trading_address}
            onChange={(e) => handleChange('trading_address', e.target.value)}
            placeholder="123 Business Street, London, EC1A 1BB"
            required
            className="mt-1.5"
            data-testid="input-address"
          />
        </div>

        {/* Time in Business */}
        <div>
          <Label className="text-sm font-medium text-slate-700">Time in Business *</Label>
          <Select value={formData.time_in_business} onValueChange={(v) => handleChange('time_in_business', v)} required>
            <SelectTrigger className="mt-1.5" data-testid="select-time-in-business">
              <SelectValue placeholder="Select duration" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0-6 months">0-6 months</SelectItem>
              <SelectItem value="6-12 months">6-12 months</SelectItem>
              <SelectItem value="1-2 years">1-2 years</SelectItem>
              <SelectItem value="2-3 years">2-3 years</SelectItem>
              <SelectItem value="3+ years">3+ years</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Loan Type */}
        <div>
          <Label className="text-sm font-medium text-slate-700">Loan Type *</Label>
          <Select value={formData.loan_type} onValueChange={(v) => handleChange('loan_type', v)} required>
            <SelectTrigger className="mt-1.5" data-testid="select-loan-type">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Secured">Secured</SelectItem>
              <SelectItem value="Unsecured">Unsecured</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Funding Purpose */}
        <div className="sm:col-span-2">
          <Label className="text-sm font-medium text-slate-700">Purpose of Funding *</Label>
          <Select value={formData.funding_purpose} onValueChange={(v) => handleChange('funding_purpose', v)} required>
            <SelectTrigger className="mt-1.5" data-testid="select-funding-purpose">
              <SelectValue placeholder="Select purpose" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Working Capital">Working Capital</SelectItem>
              <SelectItem value="Expansion">Expansion</SelectItem>
              <SelectItem value="VAT">VAT Payment</SelectItem>
              <SelectItem value="Equipment">Equipment Purchase</SelectItem>
              <SelectItem value="Cash Flow">Cash Flow</SelectItem>
              <SelectItem value="Marketing">Marketing</SelectItem>
              <SelectItem value="Stock Purchase">Stock Purchase</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Consent Checkboxes */}
      <div className="space-y-4 mb-6 p-4 bg-slate-50 rounded-xl">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="gdpr-consent-wrapper">
          <div className="flex h-5 items-center pt-0.5">
            <Checkbox
              id="gdpr_consent"
              checked={formData.gdpr_consent}
              onCheckedChange={(checked) => handleChange('gdpr_consent', checked === true)}
              data-testid="checkbox-gdpr"
            />
          </div>
          <span className="text-sm text-slate-600 leading-relaxed select-none">
            I agree to be contacted regarding my enquiry and consent to the processing of my personal data in accordance with the{' '}
            <Link to="/privacy-policy" className="text-blue-700 hover:underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</Link>. *
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer" data-testid="marketing-consent-wrapper">
          <div className="flex h-5 items-center pt-0.5">
            <Checkbox
              id="marketing_opt_in"
              checked={formData.marketing_opt_in}
              onCheckedChange={(checked) => handleChange('marketing_opt_in', checked === true)}
              data-testid="checkbox-marketing"
            />
          </div>
          <span className="text-sm text-slate-600 leading-relaxed select-none">
            I would like to receive marketing communications about products, services, and offers.
          </span>
        </label>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-blue-700 hover:bg-blue-800 text-white h-12 text-base font-medium btn-primary"
        data-testid="submit-application-btn"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          'Get My Quote'
        )}
      </Button>

      <p className="text-xs text-slate-500 mt-4 text-center">
        By submitting, you agree to our{' '}
        <Link to="/terms" className="text-blue-700 hover:underline">Terms & Conditions</Link>
      </p>
    </form>
  );
}
